/**
 * Forward-looking cashflow projection. Walks scheduled income/expense
 * items forward from `nextDate` at their declared frequency through a
 * horizon, plus future contributions from existing spread purchases.
 *
 * Pure — no React, no side effects. The card on the Plan tab calls
 * forecastCashflow() three times for 7 / 14 / 30 day horizons.
 */

import type {
  AppData,
  ScheduledFrequency,
  ScheduledItem,
  Transaction,
} from '../data/types';
import { txContributionInRange } from './budget';
import { addDays, fromIsoDate, startOfDay } from './date';
import { roundMoney } from './currency';

export interface CashflowForecast {
  /** Total scheduled + spread contributions IN (income) over the window. */
  income: number;
  /** Total scheduled + spread contributions OUT (expenses) over the window. */
  expenses: number;
  /** income - expenses. Positive = surplus, negative = shortfall. */
  net: number;
  /** Number of scheduled occurrences counted in the window. */
  occurrences: number;
}

function advance(d: Date, freq: ScheduledFrequency): Date {
  switch (freq) {
    case 'weekly':
      return addDays(d, 7);
    case 'biweekly':
      return addDays(d, 14);
    case 'monthly': {
      const next = new Date(d);
      next.setMonth(next.getMonth() + 1);
      return next;
    }
    case 'yearly': {
      const next = new Date(d);
      next.setFullYear(next.getFullYear() + 1);
      return next;
    }
    case 'one_time':
      // One-time items don't repeat; the caller stops iterating after the
      // first occurrence. Returning the same day keeps the loop terminating.
      return d;
  }
}

/**
 * Number of times `item` will fire on dates strictly after `start` and
 * on or before `end`. Walks from item.nextDate at the declared cadence;
 * a nextDate in the past is fast-forwarded to the first occurrence in
 * the window.
 */
function occurrencesInWindow(
  item: ScheduledItem,
  start: Date,
  end: Date,
): number {
  if (!item.active) return 0;
  const next = startOfDay(fromIsoDate(item.nextDate));
  if (item.frequency === 'one_time') {
    return next.getTime() > start.getTime() && next.getTime() <= end.getTime()
      ? 1
      : 0;
  }
  let cursor = next;
  // Walk forward until we're past `start`. Cap iterations to avoid
  // pathological frequencies (e.g. yearly with anchor 50 years ago).
  let safety = 1000;
  while (cursor.getTime() <= start.getTime() && safety-- > 0) {
    cursor = advance(cursor, item.frequency);
  }
  let count = 0;
  while (cursor.getTime() <= end.getTime() && safety-- > 0) {
    count++;
    cursor = advance(cursor, item.frequency);
  }
  return count;
}

/**
 * Forecast cashflow over the next `days` (starting tomorrow, inclusive
 * through `today + days`). Includes:
 *   - Scheduled items firing in the window (income or expense).
 *   - Future contributions from existing spread purchases (their
 *     remaining-days portion).
 */
export function forecastCashflow(
  data: AppData,
  today: Date,
  days: number,
): CashflowForecast {
  const t0 = startOfDay(today);
  // "Tomorrow onward" — today's already accounted for in the current
  // hero "available today" number.
  const start = addDays(t0, 1);
  const end = addDays(t0, days);

  let income = 0;
  let expenses = 0;
  let occurrences = 0;

  for (const item of data.scheduledItems) {
    if (!item.active) continue;
    const n = occurrencesInWindow(item, t0, end);
    if (n === 0) continue;
    occurrences += n;
    const total = item.amount * n;
    if (item.type === 'income') income += total;
    else expenses += total;
  }

  // Spread transactions still amortizing into the window.
  for (const t of data.transactions) {
    if (t.type !== 'expense') continue;
    if (!t.spreadDays || t.spreadDays <= 1) continue;
    expenses += txContributionInRange(t, start, end);
  }

  const net = income - expenses;
  return {
    income: roundMoney(income),
    expenses: roundMoney(expenses),
    net: roundMoney(net),
    occurrences,
  };
}

// ----- Day-by-day projected balance -----

import { scheduledOccurrencesInRange } from './scheduledOccurrences';

export interface ForecastEvent {
  type: 'income' | 'expense';
  amount: number;
  description: string;
}

export interface ForecastDay {
  date: string;
  /** End-of-day projected balance. */
  projected: number;
  events: ForecastEvent[];
}

const _dateToIso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

/**
 * Walks day-by-day from today applying scheduled occurrences plus a
 * daily-average drag for variable spend, returning per-day projected
 * balance + the events that landed on each day.
 */
export function forecastBalance(
  currentBalance: number,
  scheduledItems: ScheduledItem[],
  dailyAvgSpend: number,
  days: number = 30,
): ForecastDay[] {
  const today = startOfDay(new Date());
  const startIso = _dateToIso(today);
  const endDate = addDays(today, days - 1);
  const endIso = _dateToIso(endDate);

  const eventsByDate = new Map<string, ForecastEvent[]>();
  for (const item of scheduledItems) {
    if (!item.active) continue;
    for (const due of scheduledOccurrencesInRange(item, startIso, endIso)) {
      let arr = eventsByDate.get(due);
      if (!arr) {
        arr = [];
        eventsByDate.set(due, arr);
      }
      arr.push({ type: item.type, amount: item.amount, description: item.name });
    }
  }

  const out: ForecastDay[] = [];
  let projected = currentBalance;
  for (let i = 0; i < days; i++) {
    const d = addDays(today, i);
    const iso = _dateToIso(d);
    const events = eventsByDate.get(iso) ?? [];
    for (const e of events) {
      projected += e.type === 'income' ? e.amount : -e.amount;
    }
    projected -= dailyAvgSpend;
    out.push({
      date: iso,
      projected: Math.round(projected * 100) / 100,
      events,
    });
  }
  return out;
}

// ----- Detailed day-by-day forecast (per-event breakdown) -----
//
// Distinct from forecastBalance() above: this version tracks every
// scheduled-occurrence + the daily-average drag as separate events on
// each ForecastDay so the chart can render bill / income markers and
// the review pass can audit what's driving the curve.

export interface ForecastEventDetailed {
  type: 'income' | 'bill' | 'avgSpend';
  /** Always positive; the type determines whether it adds or subtracts. */
  amount: number;
  label: string;
  sourceId?: string;
}

export interface ForecastDayDetailed {
  date: string;
  balanceStart: number;
  balanceEnd: number;
  events: ForecastEventDetailed[];
  /** Reserved — every entry is currently projected. */
  isProjected: boolean;
}

export interface ForecastResult {
  days: ForecastDayDetailed[];
  minBalance: number;
  minBalanceDate: string;
  daysBelowZero: number;
  endingBalance: number;
}

/**
 * Per-day projected balance with per-event breakdown. Walks every day
 * in [startDate, startDate + days) applying each scheduled occurrence
 * and (optionally) a fixed daily-average drag for variable spending.
 *
 * `scheduledIncome` and `scheduledBills` may overlap with the same
 * underlying ScheduledItem array — the caller is responsible for
 * filtering by `type` before passing them in.
 */
export function buildForecast(args: {
  startBalance: number;
  startDate: string;
  days: number;
  scheduledIncome: ScheduledItem[];
  scheduledBills: ScheduledItem[];
  avgDailySpend: number;
  includeAvgSpend: boolean;
}): ForecastResult {
  const startDate = startOfDay(fromIsoDate(args.startDate));
  const startIso = _dateToIso(startDate);
  const endDate = addDays(startDate, Math.max(0, args.days - 1));
  const endIso = _dateToIso(endDate);

  const incomeByDate = new Map<string, ForecastEventDetailed[]>();
  for (const item of args.scheduledIncome) {
    if (item.type !== 'income') continue;
    if (!item.active) continue;
    for (const due of scheduledOccurrencesInRange(item, startIso, endIso)) {
      const arr = incomeByDate.get(due) ?? [];
      arr.push({
        type: 'income',
        amount: item.amount,
        label: item.name,
        sourceId: item.id,
      });
      incomeByDate.set(due, arr);
    }
  }

  const billsByDate = new Map<string, ForecastEventDetailed[]>();
  for (const item of args.scheduledBills) {
    if (item.type !== 'expense') continue;
    if (!item.active) continue;
    for (const due of scheduledOccurrencesInRange(item, startIso, endIso)) {
      const arr = billsByDate.get(due) ?? [];
      arr.push({
        type: 'bill',
        amount: item.amount,
        label: item.name,
        sourceId: item.id,
      });
      billsByDate.set(due, arr);
    }
  }

  const days: ForecastDayDetailed[] = [];
  let runningBalance = args.startBalance;
  let minBalance = args.startBalance;
  let minBalanceDate = startIso;
  let daysBelowZero = 0;

  for (let i = 0; i < args.days; i++) {
    const d = addDays(startDate, i);
    const iso = _dateToIso(d);
    const balanceStart = runningBalance;
    const events: ForecastEventDetailed[] = [
      ...(incomeByDate.get(iso) ?? []),
      ...(billsByDate.get(iso) ?? []),
    ];
    if (args.includeAvgSpend && args.avgDailySpend > 0) {
      events.push({
        type: 'avgSpend',
        amount: args.avgDailySpend,
        label: 'Avg daily spend',
      });
    }
    let delta = 0;
    for (const e of events) {
      delta += e.type === 'income' ? e.amount : -e.amount;
    }
    runningBalance =
      Math.round((balanceStart + delta) * 100) / 100;
    if (runningBalance < minBalance) {
      minBalance = runningBalance;
      minBalanceDate = iso;
    }
    if (runningBalance < 0) daysBelowZero += 1;
    days.push({
      date: iso,
      balanceStart: Math.round(balanceStart * 100) / 100,
      balanceEnd: runningBalance,
      events,
      isProjected: true,
    });
  }

  return {
    days,
    minBalance,
    minBalanceDate,
    daysBelowZero,
    endingBalance: runningBalance,
  };
}

/**
 * Trailing daily-average expense spend over `lookbackDays` ending the
 * day before `asOfDate`. Excludes transactions tagged with
 * `generatedFromScheduledId` so scheduled bills aren't double-counted
 * (they re-appear as scheduled events in `buildForecast`).
 */
export function computeAvgDailySpend(
  transactions: Transaction[],
  asOfDate: string,
  lookbackDays: number = 30,
): number {
  if (lookbackDays <= 0) return 0;
  const asOf = startOfDay(fromIsoDate(asOfDate));
  const start = addDays(asOf, -lookbackDays);
  const startIso = _dateToIso(start);
  let total = 0;
  for (const t of transactions) {
    if (t.type !== 'expense') continue;
    if (t.generatedFromScheduledId) continue;
    if (t.date >= startIso && t.date < asOfDate) {
      total += t.amount;
    }
  }
  return Math.round((total / lookbackDays) * 100) / 100;
}
